import React, { useState, useRef, useEffect } from 'react';
import { InlineCalendar } from './InlineCalendar';

interface InlineDatePickerProps {
  value?: string; // YYYY-MM-DD format or ISO string
  onSave: (newValue: string) => void;
  placeholder?: string;
  className?: string;
  displayClassName?: string;
  disabled?: boolean;
  renderDisplay?: (value: string | undefined) => React.ReactNode;
}

export const InlineDatePicker: React.FC<InlineDatePickerProps> = ({
  value,
  onSave,
  placeholder = '点击设置日期...',
  className = '',
  displayClassName = '',
  disabled = false,
  renderDisplay
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // 将value转换为YYYY-MM-DD格式
  const normalizedValue = value ? (
    value.includes('T') ? new Date(value).toISOString().split('T')[0] : value
  ) : '';

  // 处理日期选择
  const handleDateChange = async (newDate: string) => {
    try {
      setIsSaving(true);
      if (newDate === '') {
        // 清除日期
        await onSave('');
      } else {
        // 设置新日期，转换为ISO字符串
        const dateValue = new Date(newDate + 'T00:00:00.000Z').toISOString();
        await onSave(dateValue);
      }
      setIsOpen(false);
    } catch (error) {
      console.error('保存日期失败:', error);
    } finally {
      setIsSaving(false);
    }
  };

  // 点击外部关闭
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  // 处理键盘事件
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!isOpen) return;

      if (e.key === 'Escape') {
        e.preventDefault();
        setIsOpen(false);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen]);

  // 格式化显示日期
  const formatDisplayDate = (dateValue?: string) => {
    if (!dateValue) return '';
    const date = new Date(dateValue);
    return date.toLocaleDateString('zh-CN', {
      month: 'short',
      day: 'numeric'
    });
  };

  return (
    <div className={`relative ${className} ${isOpen ? 'z-selector-active' : ''}`} ref={containerRef}>
      {/* 显示区域 */}
      <div
        className={`
          cursor-pointer hover:bg-gray-50 rounded px-1 py-0.5 transition-colors
          ${disabled ? 'cursor-not-allowed opacity-50' : ''}
          ${displayClassName}
        `}
        onClick={() => !disabled && setIsOpen(!isOpen)}
        title={disabled ? '' : '点击选择日期'}
      >
        {renderDisplay ? (
          renderDisplay(value)
        ) : (
          <span className={value ? '' : 'text-gray-400 italic'}>
            {value ? formatDisplayDate(value) : placeholder}
          </span>
        )}
      </div>

      {/* 日期选择器弹出层 */}
      {isOpen && (
        <>
          <div className="popup-overlay" />
          <div className="popup-selector !p-0 !min-w-0 w-auto right-0" style={{ zIndex: 10001 }}>
            {isSaving && (
              <div className="flex items-center justify-center p-3">
                <div className="w-4 h-4 border-2 border-blue-500 border-t-transparent rounded-full animate-spin mr-2"></div>
                <span className="text-sm text-gray-600">保存中...</span>
              </div>
            )}

            {!isSaving && (
              <div style={{ width: '200px' }}>
                <InlineCalendar
                  value={normalizedValue}
                  onChange={handleDateChange}
                  className="border-0 shadow-none p-0"
                />
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};